import {
  FormField,
  Select,
} from '@cloudscape-design/components';
import {
  PartTypes,
} from '@parallel-fluidics/shared/constants';
import { decodePPartId } from '@parallel-fluidics/shared/models/projects/parts';
import PPartDisplay from 'components/Projects/pPartDisplay';
import { useGetProjectParts } from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
} from 'react';

export default function StepSelector({
  stepOptions,
  selectedStepValue = null,
  projectDocId,
  onChange,
  onValueChanged = () => {},
  error = null,
}) {
  // query all the product type of parts based on the projectDocId
  const { data: productParts, isSuccess } = useGetProjectParts({
    projectDocId,
    type: PartTypes.PRODUCT,
  });

  const options = useMemo(() => {
    if (!stepOptions?.length || !isSuccess) return stepOptions;
    return stepOptions.map((option) => {
      let findPart;
      if (option.pPartDocId) {
        findPart = productParts?.find((part) => part.id === option.pPartDocId);
      } else if (option.pPartId) {
        const { partId, revision, material } = decodePPartId(option.pPartId);
        findPart = productParts?.find((part) => part.partId === partId
          && part.revision === revision
          && part.material === material);
      }
      const name = findPart?.name || option.fallbackName;
      return {
        ...option,
        label: `${name}${option.pPartId ? ` (${option.pPartId})` : ''}`,
        linkedParts: findPart?.linkedParts || [],
        // text version is used to store in the log
        descriptionText: findPart?.notes || option.fallbackNote,
        // react node version is used to display in the UI
        description: (
          <PPartDisplay
            fieldName="notes"
            projectDocId={projectDocId}
            pPartDocId={findPart?.id}
            fallbackMessage={findPart?.notes || option.fallbackNote}
          />
        ),
      };
    });
  }, [stepOptions, isSuccess, projectDocId, productParts]);

  const selectedStep = useMemo(() => {
    if (selectedStepValue === null || !options?.length) return null;
    return options.find((opt) => opt.value === selectedStepValue) || null;
  }, [options, selectedStepValue]);

  useEffect(() => {
    onValueChanged(selectedStep);
  }, [onValueChanged, selectedStep]);

  return (
    <FormField
      label="Select Step"
      errorText={error}
    >
      <Select
        placeholder="Choose current step"
        options={options}
        onChange={onChange}
        selectedOption={selectedStep}
        triggerVariant="option"
      />
    </FormField>
  );
}

StepSelector.propTypes = {
  stepOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    pPartId: PropTypes.string,
    pPartDocId: PropTypes.string,
    disabled: PropTypes.bool,
    fallbackName: PropTypes.string,
    fallbackNote: PropTypes.string,
  })).isRequired,
  selectedStepValue: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onValueChanged: PropTypes.func,
  projectDocId: PropTypes.string.isRequired,
  error: PropTypes.string,
};
